import { Log } from './logger'
import { rehostNotificationRegion } from './notifications'

/** Coordinates reduced-motion-aware opening and closing for shared dialogs. */
const transitionDuration = 140
const closingDialogs = new WeakMap<HTMLDialogElement, Promise<void>>()

/**
 * Opens a modal after exposing one initial frame for its fade-in transition.
 */
export function openDialog(dialog: HTMLDialogElement): void {
  Log.notice('Open a shared dialog', () => {
    if (dialog.open) {
      return
    }

    dialog.dataset.dialogState = 'opening'
    dialog.showModal()
    rehostNotificationRegion()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (dialog.open && dialog.dataset.dialogState === 'opening') {
          dialog.dataset.dialogState = 'open'
        }
      })
    })
  }, 'Inspect the dialog element state and browser top-layer support.')
}

/**
 * Fades out a modal and its backdrop before removing it from the top layer.
 */
export function closeDialog(dialog: HTMLDialogElement): Promise<void> {
  return Log.notice('Close a shared dialog', () => {
    const pendingClose = closingDialogs.get(dialog)

    if (pendingClose) {
      return pendingClose
    }

    if (!dialog.open) {
      return Promise.resolve()
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close()
      delete dialog.dataset.dialogState

      return Promise.resolve()
    }

    dialog.dataset.dialogState = 'closing'

    const closing = new Promise<void>((resolve) => {
      let completed = false

      /** Completes the close exactly once after animation or timeout. */
      const finish = (): void => {
        if (completed) {
          return
        }

        completed = true
        dialog.removeEventListener('transitionend', finishTransition)

        if (dialog.open) {
          dialog.close()
        }

        delete dialog.dataset.dialogState
        closingDialogs.delete(dialog)
        resolve()
      }

      /** Completes closing when the dialog opacity transition ends. */
      const finishTransition = (event: TransitionEvent): void => {
        if (event.target === dialog && event.propertyName === 'opacity') {
          finish()
        }
      }

      dialog.addEventListener('transitionend', finishTransition)
      window.setTimeout(finish, transitionDuration + 60)
    })

    closingDialogs.set(dialog, closing)

    return closing
  }, 'Inspect the dialog transition state and transition-end event handling.')
}

const backdropDialogs = new WeakSet<HTMLDialogElement>()

/**
 * Closes a shared dialog with its transition when a click lands on the
 * backdrop outside its panel, binding each dialog only once.
 */
export function closeOnBackdropClick(dialog: HTMLDialogElement): void {
  if (backdropDialogs.has(dialog)) {
    return
  }

  backdropDialogs.add(dialog)
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) {
      return
    }

    const bounds = dialog.getBoundingClientRect()
    const clickedOutside = event.clientX < bounds.left
      || event.clientX > bounds.right
      || event.clientY < bounds.top
      || event.clientY > bounds.bottom

    if (clickedOutside) {
      void Log.info('Close a shared dialog from its backdrop', () => closeDialog(dialog))
    }
  })
}
